import React, { useState, useEffect } from 'react';
import { getCredentials, setCredentials, isConfigured, clearCredentials } from '../services/titlepointApi';
import { buildCreateService3XML, buildGetRequestSummariesXML, buildGetResultByIdXML, parseSOAPResponse } from '../services/titlepointApi';
import './TitlePointAPITester.css';

function TitlePointAPITester({ onBack }) {
  // Property search parameters
  const [address, setAddress] = useState('111 Lafayette Ave., NE');
  const [city, setCity] = useState('Grand Rapids');
  const [zip, setZip] = useState('49503');
  const [state, setState] = useState('MI');
  const [county, setCounty] = useState('Kent');
  const [apn, setApn] = useState('41-14-30-178-004');
  const [customerRef, setCustomerRef] = useState('');
  const [orderNo, setOrderNo] = useState('');

  // API Configuration
  const [serviceUrl, setServiceUrl] = useState('https://titlepoint.com/TitlePointServices/TpsService.asmx');

  // Credentials
  const [credentialsConfigured, setCredentialsConfigured] = useState(false);
  const [showCredentialsSetup, setShowCredentialsSetup] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [department, setDepartment] = useState('');
  const [titleOfficer, setTitleOfficer] = useState('');

  // Workflow state
  const [requestId, setRequestId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [resultId, setResultId] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [showRawRequest, setShowRawRequest] = useState(true);
  const [showRawResponse, setShowRawResponse] = useState(true);

  useEffect(() => {
    // Fetch credentials from backend
    const fetchCredentials = async () => {
      try {
        const response = await fetch('/api/titlepoint/credentials', {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.configured && data.username) {
            // Set credentials in sessionStorage
            setCredentials(
              data.username,
              data.password || '',
              data.serviceUrl || serviceUrl,
              data.company || '',
              data.department || '',
              data.titleOfficer || ''
            );
            setCredentialsConfigured(true);
            setUsername(data.username);
            setCompany(data.company || '');
            setDepartment(data.department || '');
            setTitleOfficer(data.titleOfficer || '');
            if (data.serviceUrl) setServiceUrl(data.serviceUrl);
            return;
          }
        }
      } catch (error) {
        console.error('Failed to fetch TitlePoint credentials:', error);
      }

      // Fallback to local sessionStorage
      const configured = isConfigured();
      setCredentialsConfigured(configured);

      if (configured) {
        const creds = getCredentials();
        setServiceUrl(creds.serviceUrl);
        setUsername(creds.username);
        setCompany(creds.company);
        setDepartment(creds.department);
        setTitleOfficer(creds.titleOfficer);
      }
    };

    fetchCredentials();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleSetupCredentials = () => {
    if (!username.trim() || !password.trim()) {
      alert('Username and password are required');
      return;
    }

    setCredentials(username, password, serviceUrl, company, department, titleOfficer);
    setCredentialsConfigured(true);
    setShowCredentialsSetup(false);
    setPassword(''); // Clear password from state
  };

  const handleClearCredentials = () => {
    if (window.confirm('Clear stored TitlePoint credentials?')) {
      clearCredentials();
      setCredentialsConfigured(false);
      setUsername('');
      setPassword('');
      setCompany('');
      setDepartment('');
      setTitleOfficer('');
    }
  };

  const addToHistory = (callType, requestXML, responseData, success = true, error = null) => {
    const historyEntry = {
      timestamp: new Date().toISOString(),
      callType,
      requestXML,
      responseData,
      success,
      error,
      metadata: {
        requestId: requestId || 'N/A',
        orderId: orderId || 'N/A',
        resultId: resultId || 'N/A',
        address: `${address}, ${city}, ${state} ${zip}`
      }
    };

    setCallHistory(prev => [historyEntry, ...prev]);
    return historyEntry;
  };

  const callTitlePointAPI = async (soapXML, soapAction, callType) => {
    const credentials = getCredentials();

    try {
      const response = await fetch('/api/titlepoint/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: serviceUrl,
          soapXML,
          soapAction,
          credentials
        })
      });

      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = {
          error: 'Failed to parse response',
          rawResponse: responseText
        };
      }

      const success = response.ok && data.success;
      addToHistory(callType, soapXML, data, success, data.error);

      return { success, data, response };
    } catch (error) {
      addToHistory(callType, soapXML, null, false, error.message);
      throw error;
    }
  };

  // Step 1: Create Service
  const handleCreateService = async () => {
    if (!address.trim()) {
      alert('Please enter an address');
      return;
    }

    setLoading(true);

    try {
      const credentials = getCredentials();
      const searchParams = {
        address: address.trim(),
        city: city.trim(),
        zip: zip.trim(),
        state,
        county,
        apn: apn.trim(),
        customerRef: customerRef.trim(),
        orderNo: orderNo.trim()
      };

      const soapXML = buildCreateService3XML(searchParams, credentials);
      const result = await callTitlePointAPI(
        soapXML,
        'http://www.TitlePoint.com/CreateService3',
        'CreateService3'
      );

      if (result.success && result.data.responseXML) {
        const parsed = parseSOAPResponse(result.data.responseXML);

        console.log('Parsed SOAP response:', parsed);

        // Try multiple extraction paths for TitlePoint response
        let extractedRequestId = null;
        let extractedOrderId = null;

        // Path 1: Standard SOAP structure
        const body = parsed.data?.['soap:Body'] || parsed.data?.Body || parsed.data?.['SOAP-ENV:Body'];
        if (body) {
          const createResponse = body?.CreateService3Response || body?.['CreateService3Response'] || body?.['ns1:CreateService3Response'];
          const createResult = createResponse?.CreateService3Result || createResponse?.['CreateService3Result'] || createResponse?.['ns1:CreateService3Result'];

          if (createResult) {
            // Try different property names
            extractedRequestId = createResult.RequestID || createResult.requestId || createResult.requestID ||
                                 createResult['#text'] || createResult._text;
            extractedOrderId = createResult.OrderID || createResult.orderId || createResult.orderID;
          }
        }

        // Path 2: Direct text content (if response is just a string/number)
        if (!extractedRequestId && typeof parsed.data === 'object') {
          // Search through all object properties recursively
          const findValue = (obj, keys) => {
            if (!obj || typeof obj !== 'object') return null;
            for (const key of keys) {
              if (obj[key]) return obj[key];
            }
            for (const value of Object.values(obj)) {
              const found = findValue(value, keys);
              if (found) return found;
            }
            return null;
          };

          extractedRequestId = findValue(parsed.data, ['RequestID', 'requestId', 'requestID', 'request_id']);
          extractedOrderId = findValue(parsed.data, ['OrderID', 'orderId', 'orderID', 'order_id']);
        }

        if (extractedRequestId) setRequestId(extractedRequestId);
        if (extractedOrderId) setOrderId(extractedOrderId);

        const message = extractedRequestId || extractedOrderId
          ? `Service created successfully!\n${extractedRequestId ? `Request ID: ${extractedRequestId}\n` : ''}${extractedOrderId ? `Order ID: ${extractedOrderId}` : ''}`
          : 'Service created. Response received but Request/Order ID not found in expected format.\n\nCheck the Call History below to see the full response structure.';

        alert(message);
      }
    } catch (error) {
      alert(`Failed to create service: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Check Status
  const handleCheckStatus = async () => {
    if (!requestId.trim()) {
      alert('Please enter a Request ID or create a service first');
      return;
    }

    setLoading(true);

    try {
      const credentials = getCredentials();
      const soapXML = buildGetRequestSummariesXML(requestId, credentials);
      const result = await callTitlePointAPI(
        soapXML,
        'http://www.TitlePoint.com/GetRequestSummaries',
        'GetRequestSummaries'
      );

      if (result.success && result.data.responseXML) {
        const parsed = parseSOAPResponse(result.data.responseXML);

        // Try to extract status and resultId
        const body = parsed.data?.['soap:Body'] || parsed.data?.Body;
        const statusResponse = body?.GetRequestSummariesResponse || body?.['GetRequestSummariesResponse'];

        alert('Status check completed. Review the response in the call history below.');
      }
    } catch (error) {
      alert(`Failed to check status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Get Results
  const handleGetResults = async () => {
    if (!resultId.trim()) {
      alert('Please enter a Result ID (from status check)');
      return;
    }

    setLoading(true);

    try {
      const credentials = getCredentials();
      const soapXML = buildGetResultByIdXML(resultId, credentials);
      const result = await callTitlePointAPI(
        soapXML,
        'http://www.TitlePoint.com/GetResultById',
        'GetResultById'
      );

      if (result.success) {
        alert('Results retrieved. Review the response in the call history below.');
      }
    } catch (error) {
      alert(`Failed to get results: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-poll status
  const handleStartPolling = () => {
    if (!requestId.trim()) {
      alert('Please enter a Request ID first');
      return;
    }

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
      return;
    }

    const interval = setInterval(async () => {
      try {
        const credentials = getCredentials();
        const soapXML = buildGetRequestSummariesXML(requestId, credentials);
        await callTitlePointAPI(
          soapXML,
          'http://www.TitlePoint.com/GetRequestSummaries',
          'GetRequestSummaries (Auto-poll)'
        );
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000); // Poll every 5 seconds

    setPollingInterval(interval);
  };

  const clearHistory = () => {
    if (window.confirm('Clear all call history?')) {
      setCallHistory([]);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatXML = (xmlString) => {
    try {
      if (!xmlString) return '';

      const formatted = xmlString
        .replace(/></g, '>\n<')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .join('\n');

      return formatted;
    } catch (e) {
      return xmlString;
    }
  };

  return (
    <div className="titlepoint-api-tester">
      <div className="tester-header">
        <div>
          <h2>TitlePoint API Tester</h2>
          <p className="tester-subtitle">
            Step-by-step SOAP API workflow with verbose logging
          </p>
        </div>
      </div>

      {/* Credentials Setup Warning */}
      {!credentialsConfigured && (
        <div className="credentials-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <h3>TitlePoint Credentials Required</h3>
            <p>Configure your TitlePoint API credentials to begin testing.</p>
            <button
              className="setup-button"
              onClick={() => setShowCredentialsSetup(true)}
            >
              Configure Credentials
            </button>
          </div>
        </div>
      )}

      {/* Credentials Setup Modal */}
      {showCredentialsSetup && (
        <div className="credentials-modal">
          <div className="credentials-modal-content">
            <h3>Configure TitlePoint Credentials</h3>

            <div className="form-group">
              <label>Username <span className="required">*</span></label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                placeholder="Your TitlePoint username"
              />
            </div>

            <div className="form-group">
              <label>Password <span className="required">*</span></label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="Your TitlePoint password"
              />
            </div>

            <div className="form-group">
              <label>Service URL</label>
              <input
                type="text"
                value={serviceUrl}
                onChange={(e) => setServiceUrl(e.target.value)}
                className="form-input"
                placeholder="https://titlepoint.com/TitlePointServices/TpsService.asmx"
              />
            </div>

            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="form-input"
                placeholder="Your company name"
              />
            </div>

            <div className="form-group">
              <label>Department</label>
              <input
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="form-input"
                placeholder="Department"
              />
            </div>

            <div className="form-group">
              <label>Title Officer</label>
              <input
                type="text"
                value={titleOfficer}
                onChange={(e) => setTitleOfficer(e.target.value)}
                className="form-input"
                placeholder="Title officer name"
              />
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowCredentialsSetup(false);
                  setPassword('');
                }}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleSetupCredentials}
                disabled={!username.trim() || !password.trim()}
              >
                Save Credentials
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tester-container">
        {/* Configuration Section */}
        {credentialsConfigured && (
          <div className="config-section">
            <h3>TitlePoint Configuration</h3>
            <div className="config-display">
              <div><strong>Service URL:</strong> <code>{serviceUrl}</code></div>
              <div><strong>Username:</strong> <code>{username}</code></div>
              <div><strong>Company:</strong> <code>{company || 'Not set'}</code></div>
              <button className="clear-button" onClick={handleClearCredentials}>
                Clear Credentials
              </button>
            </div>
          </div>
        )}

        {/* Property Search Parameters */}
        <div className="search-section">
          <h3>Property Information</h3>

          <div className="form-group">
            <label htmlFor="address">Street Address <span className="required">*</span></label>
            <input
              type="text"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g., 123 Main St"
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g., Grand Rapids"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="zip">ZIP Code</label>
              <input
                type="text"
                id="zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="e.g., 49503"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="state">State</label>
              <select
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="form-select"
              >
                <option value="MI">Michigan</option>
                <option value="OH">Ohio</option>
                <option value="IN">Indiana</option>
                <option value="IL">Illinois</option>
                <option value="WI">Wisconsin</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="county">County</label>
              <input
                type="text"
                id="county"
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                placeholder="e.g., Kent"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="apn">APN (Optional)</label>
            <input
              type="text"
              id="apn"
              value={apn}
              onChange={(e) => setApn(e.target.value)}
              placeholder="e.g., 41-14-01-100-001"
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerRef">Customer Reference</label>
              <input
                type="text"
                id="customerRef"
                value={customerRef}
                onChange={(e) => setCustomerRef(e.target.value)}
                placeholder="Optional customer reference"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="orderNo">Order Number</label>
              <input
                type="text"
                id="orderNo"
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                placeholder="Optional order number"
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Workflow Steps */}
        <div className="workflow-section">
          <h3>API Workflow Steps</h3>

          <div className="workflow-steps">
            {/* Step 1: Create Service */}
            <div className="workflow-step">
              <div className="step-header">
                <span className="step-number">1</span>
                <div className="step-info">
                  <h4>Create Service</h4>
                  <p>Submit property search to TitlePoint (CreateService3)</p>
                </div>
              </div>
              <button
                className="test-button"
                onClick={handleCreateService}
                disabled={loading || !credentialsConfigured || !address.trim()}
              >
                {loading ? '⏳ Creating...' : '🔄 Create Service'}
              </button>
              {requestId && (
                <div className="step-result">
                  <strong>Request ID:</strong> <code>{requestId}</code>
                  {orderId && <><br/><strong>Order ID:</strong> <code>{orderId}</code></>}
                </div>
              )}
            </div>

            {/* Step 2: Check Status */}
            <div className="workflow-step">
              <div className="step-header">
                <span className="step-number">2</span>
                <div className="step-info">
                  <h4>Check Status</h4>
                  <p>Poll for request completion (GetRequestSummaries)</p>
                </div>
              </div>
              <div className="step-controls">
                <input
                  type="text"
                  value={requestId}
                  onChange={(e) => setRequestId(e.target.value)}
                  placeholder="Request ID from Step 1"
                  className="form-input"
                />
                <button
                  className="test-button"
                  onClick={handleCheckStatus}
                  disabled={loading || !credentialsConfigured || !requestId.trim()}
                >
                  {loading ? '⏳ Checking...' : '🔍 Check Status'}
                </button>
                <button
                  className={pollingInterval ? 'clear-button' : 'test-button'}
                  onClick={handleStartPolling}
                  disabled={!credentialsConfigured || !requestId.trim()}
                >
                  {pollingInterval ? '⏸️ Stop Auto-Poll' : '▶️ Auto-Poll (5s)'}
                </button>
              </div>
            </div>

            {/* Step 3: Get Results */}
            <div className="workflow-step">
              <div className="step-header">
                <span className="step-number">3</span>
                <div className="step-info">
                  <h4>Get Results</h4>
                  <p>Retrieve search results (GetResultById)</p>
                </div>
              </div>
              <div className="step-controls">
                <input
                  type="text"
                  value={resultId}
                  onChange={(e) => setResultId(e.target.value)}
                  placeholder="Result ID from status check"
                  className="form-input"
                />
                <button
                  className="test-button"
                  onClick={handleGetResults}
                  disabled={loading || !credentialsConfigured || !resultId.trim()}
                >
                  {loading ? '⏳ Getting...' : '📥 Get Results'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Call History */}
        {callHistory.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Call History ({callHistory.length})</h3>
              <div className="view-toggles">
                <label>
                  <input
                    type="checkbox"
                    checked={showRawRequest}
                    onChange={(e) => setShowRawRequest(e.target.checked)}
                  />
                  Show Request
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={showRawResponse}
                    onChange={(e) => setShowRawResponse(e.target.checked)}
                  />
                  Show Response
                </label>
                <button
                  className="clear-button"
                  onClick={clearHistory}
                >
                  Clear History
                </button>
              </div>
            </div>

            {callHistory.map((entry, index) => (
              <div
                key={index}
                className={`result-card ${entry.success ? 'success' : 'error'}`}
              >
                <div className="result-header">
                  <div className="result-info">
                    <span className={`status-badge ${entry.success ? 'success' : 'error'}`}>
                      {entry.success ? '✓ Success' : '✗ Error'}
                    </span>
                    <span className="call-type">{entry.callType}</span>
                    <span className="timestamp">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="result-config">
                    <span><strong>Address:</strong> {entry.metadata.address}</span>
                    <span><strong>Request ID:</strong> {entry.metadata.requestId}</span>
                  </div>
                </div>

                {showRawRequest && (
                  <div className="result-data">
                    <div className="data-header">
                      <strong>SOAP Request:</strong>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(entry.requestXML)}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <details className="status-details">
                      <summary>View Request XML</summary>
                      <pre className="data-content xml-content">
                        {formatXML(entry.requestXML)}
                      </pre>
                    </details>
                  </div>
                )}

                {showRawResponse && entry.responseData && (
                  <div className="result-data">
                    <div className="data-header">
                      <strong>SOAP Response:</strong>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(
                          entry.responseData.responseXML || JSON.stringify(entry.responseData, null, 2)
                        )}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <details className="status-details" open>
                      <summary>View Response XML</summary>
                      <pre className="data-content xml-content">
                        {formatXML(entry.responseData.responseXML) || JSON.stringify(entry.responseData, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}

                {entry.error && (
                  <div className="error-message">
                    <strong>Error:</strong> {entry.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="help-section">
          <h3>TitlePoint API Workflow</h3>
          <ol>
            <li><strong>Create Service:</strong> Submit a property search request. TitlePoint returns a Request ID and Order ID.</li>
            <li><strong>Check Status:</strong> Poll the Request ID to check if the search is complete. Use auto-poll for convenience.</li>
            <li><strong>Get Results:</strong> Once complete, use the Result ID from the status check to retrieve the full search results.</li>
          </ol>
          <p>
            All API calls are logged in the Call History section below with full request/response data for debugging.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TitlePointAPITester;
